import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * /v2/super/auth/* — the super-admin Auth console (PD-1512, AUTH_MIGRATION.md §1a.4).
 * Every endpoint is super-admin only, enforced SERVER-side in the handlers (the /super
 * route is only gated client-side). Destructive actions are audited with the acting
 * admin's profile reference.
 */

export const authAdminOverview = oc
  .route({ method: 'GET', path: '/v2/super/auth/overview' })
  .input(z.object({}).optional())
  .output(
    z.object({
      sessions: z.number(),
      providerSessions: z.number(),
      patientSessions: z.number(),
      activeInLast5Min: z.number(),
      lockedAccounts: z.number(),
      loginsInFlight: z.number(),
      trustedDevices: z.number(),
      otpOverrides: z.number(),
    }),
  )

// Model paging (mirrors ModelPageRequest / listPatients in src/patient/handlers/
// list-patients.contract.ts): the request carries `page: { offset, count, limit }`,
// the response echoes it extended with `size` and `total`. Inlined rather than imported
// from the model layer because this contract is bundled into the web client.
const pageRequestSchema = z.object({
  offset: z.number().int().min(0),
  count: z.number().int().min(0),
  limit: z.number().int().min(0),
})

const pageSchema = pageRequestSchema.extend({
  size: z.number().int().min(0),
  total: z.number().int().min(0),
})

const sessionSchema = z.object({
  id: z.number(),
  userId: z.string(),
  profileRef: z.string(),
  profileName: z.string().optional(),
  sessionKind: z.enum(['provider', 'patient']),
  /** Credential channel — 'native' rows are mobile-app sessions (PD-1542). */
  clientType: z.enum(['web', 'native']),
  deviceLabel: z.string().optional(),
  createdIp: z.string().optional(),
  createdAt: z.string(),
  lastRefreshAt: z.string(),
  /**
   * Effective expiry: the absolute policy anchor, except for refresh-less sessions
   * (super admins), whose only access token can run out first.
   */
  expiresAt: z.string(),
  /** Row is provably dead (absolute/idle/refresh-less token expiry) but not yet purged. */
  expired: z.boolean(),
})

export const authAdminSessions = oc
  .route({ method: 'POST', path: '/v2/super/auth/sessions/list' })
  .input(z.object({ page: pageRequestSchema }))
  .output(z.object({ page: pageSchema, data: z.array(sessionSchema) }))

/** Atomic: upstream Login revoked first; on failure the session is kept and 503 returned — retry. */
export const authAdminRevokeSession = oc
  .route({ method: 'POST', path: '/v2/super/auth/sessions/revoke' })
  .input(z.object({ sessionId: z.number().int() }))
  .output(z.object({ ok: z.literal(true) }))

export const authAdminRevokeUserSessions = oc
  .route({ method: 'POST', path: '/v2/super/auth/sessions/revoke-user' })
  .input(z.object({ userId: z.string().min(1) }))
  .output(
    z.object({
      revoked: z.number(),
      /** Sessions whose upstream revoke failed — kept in the list, retryable. */
      failed: z.number(),
    }),
  )

export const authAdminTrustedDevices = oc
  .route({ method: 'POST', path: '/v2/super/auth/trusted-devices/list' })
  .input(z.object({ page: pageRequestSchema }))
  .output(
    z.object({
      page: pageSchema,
      data: z.array(
        z.object({
          id: z.number(),
          userId: z.string(),
          userEmail: z.string().optional(),
          deviceLabel: z.string().optional(),
          createdAt: z.string(),
          expiresAt: z.string(),
        }),
      ),
    }),
  )

export const authAdminRevokeTrustedDevice = oc
  .route({ method: 'POST', path: '/v2/super/auth/trusted-devices/revoke' })
  .input(z.object({ id: z.number().int() }))
  .output(z.object({ ok: z.literal(true) }))

export const authAdminLockouts = oc
  .route({ method: 'GET', path: '/v2/super/auth/lockouts' })
  .input(z.object({}).optional())
  .output(
    z.object({
      lockouts: z.array(
        z.object({
          emailHash: z.string(),
          failures: z.number(),
          locked: z.boolean(),
          windowMsRemaining: z.number(),
          userEmail: z.string().optional(),
          userId: z.string().optional(),
        }),
      ),
    }),
  )

export const authAdminClearLockout = oc
  .route({ method: 'POST', path: '/v2/super/auth/lockouts/clear' })
  .input(z.object({ emailHash: z.string().length(64) }))
  .output(z.object({ ok: z.literal(true) }))

export const authAdminChallenges = oc
  .route({ method: 'GET', path: '/v2/super/auth/challenges' })
  .input(z.object({}).optional())
  .output(
    z.object({
      challenges: z.array(
        z.object({
          id: z.string(),
          sessionKind: z.string(),
          step: z.string(),
          maskedDestination: z.string().optional(),
          attemptsLeft: z.number().optional(),
          msRemaining: z.number(),
        }),
      ),
    }),
  )

export const authAdminVoidChallenge = oc
  .route({ method: 'POST', path: '/v2/super/auth/challenges/void' })
  .input(z.object({ id: z.string().length(64) }))
  .output(z.object({ ok: z.literal(true) }))

/**
 * Fixed-2FA-code exception list (PD-1641): accounts whose OTP verification expects a
 * fixed code instead of the delivered one. Redis-backed with a mandatory 1–365 day TTL.
 */
export const authAdminOtpOverrides = oc
  .route({ method: 'GET', path: '/v2/super/auth/otp-overrides' })
  .input(z.object({}).optional())
  .output(
    z.object({
      overrides: z.array(
        z.object({
          emailHash: z.string(),
          email: z.string(),
          code: z.string(),
          setBy: z.string(),
          setByName: z.string().optional(),
          setAt: z.string(),
          expiresAt: z.string(),
        }),
      ),
    }),
  )

/**
 * Upsert: an existing override for the same account is replaced (code AND expiry). The
 * email must belong to a known account (404 otherwise). `expiresAt` is an absolute
 * instant — in the future, at most 365 days out (server-enforced).
 */
export const authAdminSetOtpOverride = oc
  .route({ method: 'POST', path: '/v2/super/auth/otp-overrides/set' })
  .input(
    z.object({
      email: z.string().trim().email(),
      code: z.string().regex(/^\d{6}$/, 'Code must be exactly 6 digits'),
      expiresAt: z.string().datetime(),
    }),
  )
  .output(z.object({ emailHash: z.string() }))

export const authAdminRemoveOtpOverride = oc
  .route({ method: 'POST', path: '/v2/super/auth/otp-overrides/remove' })
  .input(z.object({ emailHash: z.string().length(64) }))
  .output(z.object({ ok: z.literal(true) }))

export const authAdminAuditEvents = oc
  .route({ method: 'POST', path: '/v2/super/auth/audit/list' })
  .input(
    z.object({
      page: pageRequestSchema,
      eventType: z.string().optional(),
    }),
  )
  .output(
    z.object({
      page: pageSchema,
      data: z.array(
        z.object({
          id: z.number(),
          at: z.string(),
          eventType: z.string(),
          userId: z.string().optional(),
          userEmail: z.string().optional(),
          /** Acting super-admin's profile ref, for admin_* events (from `detail.admin`). */
          adminRef: z.string().optional(),
          adminName: z.string().optional(),
          sessionId: z.number().optional(),
          ip: z.string().optional(),
          userAgent: z.string().optional(),
          detail: z.record(z.string(), z.unknown()).optional(),
        }),
      ),
    }),
  )
